import {ReactNode} from "react";
import {OtsTooltip} from "./OtsTooltip";

export type OtsFlagTone = "danger" | "warning" | "success";

export type OtsFlaggedValueProps = {
  children: ReactNode;
  /** Without a tone the value renders plainly, so callers can pass an optional flag straight through. */
  tone?: OtsFlagTone;
  /** Why the value is flagged; shown on hover and keyboard focus. */
  message?: ReactNode;
  /** Stable identifier of the rule that flagged the value, exposed as `data-flag`. */
  rule?: string;
  className?: string;
};

export const otsFlagToneClasses: Record<OtsFlagTone, string> = {
  danger: "bg-[var(--ots-danger-container)] text-[var(--ots-on-danger-container)]",
  warning: "bg-[var(--ots-warning-container)] text-[var(--ots-on-warning-container)]",
  success: "bg-[var(--ots-success-container)] text-[var(--ots-on-success-container)]",
};

/** A table or list value highlighted by a review rule, with the reason one hover or focus away. */
export const OtsFlaggedValue = ({children, tone, message, rule, className}: OtsFlaggedValueProps) => {
  if (!tone) return <span className={["truncate", className].filter(Boolean).join(" ")}>{children}</span>;

  // The highlight bleeds 4px either side so flagged and plain values stay on the same text edge.
  const bleed = "-mx-1 w-fit min-w-0 max-w-[calc(100%+0.5rem)]";
  const value = <span
    tabIndex={message != null ? 0 : undefined}
    data-flag={rule}
    data-tone={tone}
    className={["truncate rounded px-1 font-semibold", message != null ? "min-w-0" : bleed, otsFlagToneClasses[tone], className].filter(Boolean).join(" ")}>
    {children}
  </span>;

  return message != null ? <OtsTooltip content={message} density="compact" className={bleed}>{value}</OtsTooltip> : value;
};
